import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { formatFee } from "@/lib/candidate-utils";
import { Buildings, Plus, X, MagnifyingGlass, TrendUp, Users, CaretDown, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

export default function Clients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [quickCreating, setQuickCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter by client name OR any contact's name/email/title
  const q = search.trim().toLowerCase();
  const filtered = !q
    ? clients.map((c) => ({ ...c, matched_contact: null }))
    : clients
        .map((c) => {
          const nameHit = c.name.toLowerCase().includes(q);
          const matchedContact = (c.contacts || []).find((ct) =>
            [ct.name, ct.email, ct.title].some((f) => (f || "").toLowerCase().includes(q))
          );
          if (!nameHit && !matchedContact) return null;
          return { ...c, matched_contact: nameHit ? null : matchedContact };
        })
        .filter(Boolean);

  const exactMatchExists = q && clients.some((c) => c.name.trim().toLowerCase() === q);
  const showQuickCreate = q.length > 0 && !exactMatchExists;

  const quickCreate = async () => {
    const name = search.trim();
    if (!name || quickCreating) return;
    setQuickCreating(true);
    setError("");
    try {
      const { data } = await api.post("/clients", { name });
      navigate(`/clients/${data.id}`);
    } catch (err) {
      setError(getErrorMessage(err));
      setQuickCreating(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
            <Buildings size={12} weight="bold" /> Client book
          </div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-none">
            Clients
          </h1>
          <div className="text-sm text-zinc-500 mt-3">
            Every company you've sent a candidate to. Win rates update as deals close.
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="new-client-btn"
          className="flex items-center gap-2 bg-zinc-950 text-white px-5 py-3 rounded-sm hover:bg-zinc-800 transition-colors text-sm font-medium"
        >
          <Plus size={16} weight="bold" /> New client
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="clients-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by client, contact name, email or title…"
            className="w-full bg-white border border-zinc-200 pl-9 pr-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
          />
        </div>
        {showQuickCreate && (
          <button
            type="button"
            onClick={quickCreate}
            disabled={quickCreating}
            data-testid="quick-create-client-btn"
            className="flex items-center gap-2 border border-zinc-950 bg-white text-zinc-950 px-3 py-2 rounded-sm text-sm font-medium hover:bg-zinc-950 hover:text-white transition-colors disabled:opacity-60"
          >
            <Plus size={14} weight="bold" />
            {quickCreating ? "Creating…" : <>Create &ldquo;{search.trim()}&rdquo;</>}
          </button>
        )}
      </div>

      {error && (
        <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] p-4 rounded-sm text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 border-dashed rounded-sm p-16 text-center">
          <Buildings size={32} weight="light" className="mx-auto text-zinc-300 mb-4" />
          <div className="font-heading font-bold text-lg mb-1">
            {clients.length === 0 ? "No clients yet" : "No matches"}
          </div>
          <div className="text-sm text-zinc-500 mb-6">
            {clients.length === 0
              ? "Clients are auto-created when you type an agency name on an interview slot. Or add one manually."
              : q
              ? <>No client matches &ldquo;{search.trim()}&rdquo;.</>
              : "Try a different search term."}
          </div>
          {clients.length === 0 && (
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-800"
            >
              <Plus size={14} weight="bold" /> Add first client
            </button>
          )}
          {clients.length > 0 && showQuickCreate && (
            <button
              onClick={quickCreate}
              disabled={quickCreating}
              data-testid="empty-state-create-client-btn"
              className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-60"
            >
              <Plus size={14} weight="bold" />
              {quickCreating ? "Creating…" : <>Create &ldquo;{search.trim()}&rdquo;</>}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <ClientCard key={c.id} client={c} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setShowCreate(false); navigate(`/clients/${c.id}`); }}
        />
      )}
    </div>
  );
}

function ClientCard({ client }) {
  const s = client.stats || {};
  // Derive best contact hint: matched (from search) > primary > first contact > legacy primary_contact_name
  const primaryContact = (client.contacts || []).find((c) => c.is_primary) || (client.contacts || [])[0];
  const hint = client.matched_contact || primaryContact;
  const hintText = hint ? [hint.name, hint.title].filter(Boolean).join(" · ") : (client.primary_contact_name || "");
  return (
    <Link
      to={`/clients/${client.id}`}
      data-testid={`client-card-${client.id}`}
      className={`block bg-white border rounded-sm p-5 hover:border-zinc-950 transition-colors ${client.matched_contact ? "border-emerald-400" : "border-zinc-200"}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0">
          <div className="font-heading font-bold text-lg tracking-tight truncate">{client.name}</div>
          {hintText && (
            <div className={`text-xs mt-0.5 truncate ${client.matched_contact ? "text-emerald-800 font-medium" : "text-zinc-500"}`} data-testid={`client-hint-${client.id}`}>
              {client.matched_contact && <span className="uppercase tracking-[0.15em] text-[10px] mr-1">match ·</span>}
              {hintText}
            </div>
          )}
        </div>
        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-sm ${
          s.win_rate >= 50
            ? "bg-emerald-100 text-emerald-800"
            : s.win_rate > 0
            ? "bg-amber-100 text-amber-800"
            : "border border-zinc-200 text-zinc-500"
        }`}>
          {s.win_rate || 0}% win
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <Stat label="Sent" value={s.sent || 0} />
        <Stat label="Active" value={s.active || 0} />
        <Stat label="Placed" value={s.placed || 0} accent="emerald" />
        <Stat label="Written" value={formatFee(s.fees_written || 0)} small accent="emerald" />
      </div>
    </Link>
  );
}

const Stat = ({ label, value, small, accent }) => (
  <div>
    <div className={`font-heading font-bold ${small ? "text-sm" : "text-2xl"} ${accent === "emerald" ? "text-emerald-700" : "text-zinc-950"}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">{label}</div>
  </div>
);

function CreateClientModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [contacts, setContacts] = useState([]); // {name,email,phone,title}
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const addContact = () => {
    setContacts((prev) => [...prev, { name: "", email: "", phone: "", title: "" }]);
  };
  const updateContact = (idx, key, value) => {
    setContacts((prev) => prev.map((c, i) => (i === idx ? { ...c, [key]: value } : c)));
  };
  const removeContact = (idx) => {
    setContacts((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError("");
    // Only send contacts that have at least a name
    const cleanContacts = contacts
      .map((c) => ({ ...c, name: c.name.trim(), email: c.email.trim(), phone: c.phone.trim(), title: c.title.trim() }))
      .filter((c) => c.name);
    const payload = { name: name.trim(), address: address.trim(), notes: notes.trim() };
    if (cleanContacts.length > 0) {
      cleanContacts[0].is_primary = true;
      payload.contacts = cleanContacts;
    }
    try {
      const { data } = await api.post("/clients", payload);
      onCreated(data);
    } catch (err) {
      setError(getErrorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="create-client-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Client book</div>
            <div className="font-heading font-bold text-2xl tracking-tight">New client</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950" data-testid="close-create-client-modal"><X size={20} weight="bold" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <ClientField label="Company name" required>
            <input
              data-testid="client-name-input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
              placeholder="Acme Corp"
              autoFocus
            />
            <div className="text-[11px] text-zinc-500 mt-1.5">The only required field. You can add contacts and details now, or later on the client's page.</div>
          </ClientField>

          {/* Contacts (collapsible list) */}
          <div className="border-t border-zinc-100 pt-4">
            {contacts.length === 0 ? (
              <button
                type="button"
                onClick={addContact}
                data-testid="add-first-contact-btn"
                className="w-full flex items-center justify-center gap-2 border border-dashed border-zinc-300 text-zinc-600 hover:border-zinc-950 hover:text-zinc-950 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors"
              >
                <Plus size={14} weight="bold" /> Add contact
              </button>
            ) : (
              <div className="space-y-3">
                {contacts.map((c, idx) => (
                  <div key={idx} className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/50" data-testid={`contact-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        {idx === 0 ? "Primary contact" : `Contact ${idx + 1}`}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeContact(idx)}
                        data-testid={`remove-contact-${idx}`}
                        className="text-zinc-400 hover:text-[#B91C1C]"
                        title="Remove contact"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        data-testid={`contact-name-${idx}`}
                        value={c.name}
                        onChange={(e) => updateContact(idx, "name", e.target.value)}
                        placeholder="Name"
                        className="bg-white border border-zinc-200 px-2.5 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                      />
                      <input
                        data-testid={`contact-title-${idx}`}
                        value={c.title}
                        onChange={(e) => updateContact(idx, "title", e.target.value)}
                        placeholder="Title (optional)"
                        className="bg-white border border-zinc-200 px-2.5 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                      />
                      <input
                        data-testid={`contact-email-${idx}`}
                        type="email"
                        value={c.email}
                        onChange={(e) => updateContact(idx, "email", e.target.value)}
                        placeholder="Email"
                        className="bg-white border border-zinc-200 px-2.5 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                      />
                      <input
                        data-testid={`contact-phone-${idx}`}
                        value={c.phone}
                        onChange={(e) => updateContact(idx, "phone", e.target.value)}
                        placeholder="Phone"
                        className="bg-white border border-zinc-200 px-2.5 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                      />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addContact}
                  data-testid="add-another-contact-btn"
                  className="text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-zinc-950 flex items-center gap-1"
                >
                  <Plus size={12} weight="bold" /> Add another contact
                </button>
              </div>
            )}
          </div>

          {/* Optional details (collapsible) */}
          <div className="border-t border-zinc-100 pt-4">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              data-testid="toggle-client-details-btn"
              className="w-full flex items-center justify-between text-sm text-zinc-600 hover:text-zinc-950"
            >
              <span className="uppercase tracking-[0.15em] text-[11px] font-medium">Optional details</span>
              <CaretDown size={14} weight="bold" className={`transition-transform ${showDetails ? "rotate-180" : ""}`} />
            </button>
            {showDetails && (
              <div className="space-y-3 mt-3">
                <ClientField label="Address">
                  <input
                    data-testid="client-address-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                    placeholder="Suburb, State"
                  />
                </ClientField>
                <ClientField label="Notes">
                  <textarea
                    data-testid="client-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950 resize-y"
                    placeholder="Anything worth remembering — hiring cycles, preferences, warm reason."
                  />
                </ClientField>
              </div>
            )}
          </div>

          {error && (
            <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm">{error}</div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 transition-colors text-sm">Cancel</button>
            <button type="submit" disabled={submitting || !name.trim()} data-testid="submit-create-client-btn" className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 transition-colors text-sm disabled:opacity-50">
              {submitting ? "Creating..." : "Create client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ClientField = ({ label, children, required }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
      {label}{required && <span className="text-[#FF2A2A]"> *</span>}
    </label>
    {children}
  </div>
);
